/* Vesper Cocktails: 10% off signup popup (sections/vc-signup-popup.liquid).
   ES module. Delegated handlers live on each section root; global listeners are
   registered once. State classes: .is-open on the root, html.vc-signup-open (scroll lock).
   Storage (all wrapped in try/catch):
     localStorage   vcSignup:closedAt    ms timestamp of the last close (snooze)
     localStorage   vcSignup:subscribed  '1' after a successful signup
     sessionStorage vcSignup:teaser      '1' after a close, shows the reopen pill for the session */

const html = document.documentElement;
const EMAIL_RE = /^\S+@\S+\.\S+$/;
const KEY_CLOSED = 'vcSignup:closedAt';
const KEY_SUB = 'vcSignup:subscribed';
const KEY_TEASER = 'vcSignup:teaser';
const DAY = 86400000;

const mem = {};
const store = {
  get(k) { try { return localStorage.getItem(k); } catch (_) { return mem[k] || null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch (_) { mem[k] = v; } },
  sget(k) { try { return sessionStorage.getItem(k); } catch (_) { return mem['s:' + k] || null; } },
  sset(k, v) { try { sessionStorage.setItem(k, v); } catch (_) { mem['s:' + k] = v; } },
};

const instances = new Set();

/* A modal dialog (cart drawer, search) or the mobile menu is open. */
function overlayOpen() {
  if (html.classList.contains('vc-menu-open')) return true;
  const dialogs = document.querySelectorAll('dialog[open]');
  for (const d of dialogs) {
    try { if (d.matches(':modal')) return true; } catch (_) { return true; }
  }
  return !!document.querySelector('cart-drawer-component[open]');
}

function pathSuppressed() {
  let p = location.pathname;
  const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  if (base !== '/' && p.indexOf(base) === 0) p = '/' + p.slice(base.length);
  return /^\/(cart|checkouts?|account|challenge|password)/i.test(p);
}

function focusables(scope) {
  const sel = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(scope.querySelectorAll(sel)).filter((el) => !el.hidden && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden');
}

class Signup {
  constructor(el) {
    this.el = el;
    this.sectionId = el.dataset.sectionId;
    this.design = el.dataset.designMode === 'true';
    this.delay = Math.max(0, Math.min(7, parseFloat(el.dataset.delay) || 0));
    this.snoozeDays = Math.max(0, parseFloat(el.dataset.snoozeDays) || 0);
    this.teaserOn = el.dataset.teaser === 'true';
    this.suppressedByServer = el.dataset.suppress === 'true';
    this.previewSuccess = el.dataset.previewSuccess === 'true';
    this.code = el.dataset.code || 'WELCOME10';
    this.copyLabelText = el.dataset.copyLabel || 'Copy';
    this.copiedLabelText = el.dataset.copiedLabel || 'Copied';
    this.genericError = el.dataset.errorGeneric || 'Something went wrong. Please try again.';

    this.dialog = el.querySelector('[data-vc-dialog]');
    this.form = el.querySelector('form');
    this.input = el.querySelector('[data-vc-email]');
    this.errorEl = el.querySelector('[data-vc-error]');
    this.submitBtn = el.querySelector('[data-vc-submit]');
    this.copyLabel = el.querySelector('[data-vc-copy-label]');
    this.teaser = el.querySelector('[data-vc-teaser]');
    this.states = {
      form: el.querySelector('[data-vc-state="form"]'),
      done: el.querySelector('[data-vc-state="done"]'),
    };

    this.isOpen = false;
    this.waiting = false;
    this.timer = null;
    this.prevFocus = null;
    this.busy = false;

    el.addEventListener('click', (e) => this.onClick(e));
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.form.addEventListener('input', () => this.clearError());
    }
    this.start();
  }

  /* ---------- Lifecycle ---------- */
  isSubscribed() { return store.get(KEY_SUB) === '1'; }
  isSnoozed() {
    if (!this.snoozeDays) return false;
    const t = parseInt(store.get(KEY_CLOSED) || '0', 10);
    return !!t && Date.now() - t < this.snoozeDays * DAY;
  }
  suppressed() { return this.suppressedByServer || pathSuppressed(); }

  start() {
    if (this.design) { this.syncTeaser(); return; }

    const posted = !!(this.form && this.form.querySelector('[data-vc-signup-posted]')) || /[?&]customer_posted=true/.test(location.search);
    if (posted) {
      store.set(KEY_SUB, '1');
      this.cleanUrl();
      this.syncTeaser();
      if (!pathSuppressed()) {
        this.setState('done');
        setTimeout(() => this.open(), 60); /* timeout, not rAF: rAF never fires in a background tab */
      }
      return;
    }

    this.syncTeaser();
    if (this.suppressed() || this.isSubscribed()) return;

    /* Server-side form errors after a non-AJAX post (hCaptcha fallback): reopen with the message. */
    const srv = this.serverErrorText(document);
    if (srv) {
      this.setState('form');
      this.showError(srv);
      setTimeout(() => this.open(), 60); /* timeout, not rAF: rAF never fires in a background tab */
      return;
    }

    if (this.isSnoozed()) return;
    /* The delay counts from navigation start, so the popup is up within `delay` seconds of landing. */
    const elapsed = (window.performance && performance.now) ? performance.now() : 0;
    this.timer = setTimeout(() => { this.timer = null; this.tryOpen(); }, Math.max(0, this.delay * 1000 - elapsed));
  }

  dispose() {
    clearTimeout(this.timer);
    this.timer = null;
    if (this.isOpen) { html.classList.remove('vc-signup-open'); html.style.removeProperty('--vc-signup-sbw'); }
    this.isOpen = false;
  }

  cleanUrl() {
    try {
      const u = new URL(location.href);
      if (u.searchParams.has('customer_posted')) {
        u.searchParams.delete('customer_posted');
        const hash = /^#VcSignupForm/.test(u.hash) ? '' : u.hash;
        history.replaceState(null, '', u.pathname + u.search + hash);
      }
    } catch (_) { /* no-op */ }
  }

  serverErrorText(doc) {
    const scope = (this.form && doc === document) ? this.form : doc.getElementById(this.form ? this.form.id : '');
    const node = (scope && scope.querySelector('[data-vc-signup-errors]')) || doc.querySelector('[data-vc-signup-errors]');
    const text = node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
    return text;
  }

  /* ---------- Open / close ---------- */
  tryOpen() {
    if (this.isOpen) return;
    if (overlayOpen()) { this.waiting = true; return; }
    this.waiting = false;
    this.open();
  }

  /* Called when html classes or <dialog open> change. */
  onEnvChange() {
    this.syncTeaser();
    if (this.waiting && !this.isOpen && !overlayOpen()) {
      this.waiting = false;
      /* let the menu / drawer close transition finish first */
      setTimeout(() => { if (!this.isOpen && !overlayOpen() && !this.design) this.open(); }, 450);
    }
  }

  open() {
    if (this.isOpen || !this.dialog) return;
    this.isOpen = true;
    const ae = document.activeElement;
    this.prevFocus = ae && ae !== document.body && !this.dialog.contains(ae) ? ae : null;
    const sbw = window.innerWidth - html.clientWidth;
    html.style.setProperty('--vc-signup-sbw', Math.max(0, sbw) + 'px');
    html.classList.add('vc-signup-open');
    this.el.classList.add('is-open');
    this.syncTeaser();
    void this.dialog.offsetWidth; /* flush so focus lands on a visible element */
    try { this.dialog.focus({ preventScroll: true }); } catch (_) { /* no-op */ }
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.waiting = false;
    this.el.classList.remove('is-open');
    html.classList.remove('vc-signup-open');
    html.style.removeProperty('--vc-signup-sbw');
    if (!this.design) {
      store.set(KEY_CLOSED, String(Date.now()));
      if (this.teaserOn && !this.isSubscribed()) store.sset(KEY_TEASER, '1');
    }
    this.syncTeaser();
    const back = this.prevFocus;
    this.prevFocus = null;
    if (back && document.contains(back)) {
      try { back.focus({ preventScroll: true }); } catch (_) { /* no-op */ }
    }
  }

  syncTeaser() {
    if (!this.teaser) return;
    const show = this.teaserOn && !this.design && !this.isSubscribed() && !this.suppressed() && store.sget(KEY_TEASER) === '1';
    this.teaser.hidden = !show;
    this.teaser.classList.toggle('is-blocked', overlayOpen());
  }

  /* ---------- States ---------- */
  setState(name) {
    Object.keys(this.states).forEach((k) => { if (this.states[k]) this.states[k].hidden = k !== name; });
    if (this.dialog) this.dialog.setAttribute('aria-labelledby', name === 'done' ? this.dialog.dataset.titleDone : this.dialog.dataset.titleForm);
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg || this.errorEl.dataset.defaultError || '';
    this.errorEl.hidden = false;
    if (this.input) { this.input.classList.add('is-invalid'); this.input.setAttribute('aria-invalid', 'true'); }
  }

  clearError() {
    if (!this.errorEl || this.errorEl.hidden) return;
    this.errorEl.hidden = true;
    if (this.input) { this.input.classList.remove('is-invalid'); this.input.removeAttribute('aria-invalid'); }
  }

  setBusy(on) {
    this.busy = on;
    if (!this.submitBtn) return;
    this.submitBtn.disabled = on;
    this.submitBtn.setAttribute('aria-busy', String(on));
  }

  succeed() {
    store.set(KEY_SUB, '1');
    try { sessionStorage.removeItem(KEY_TEASER); } catch (_) { /* no-op */ }
    this.setBusy(false);
    this.setState('done');
    this.syncTeaser();
    if (this.dialog) { try { this.dialog.focus({ preventScroll: true }); } catch (_) { /* no-op */ } }
  }

  /* ---------- Events ---------- */
  onClick(e) {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    if (t.closest('[data-vc-close]') || t.closest('[data-vc-overlay]')) { e.preventDefault(); this.close(); return; }
    if (t.closest('[data-vc-open]')) { e.preventDefault(); this.setState('form'); this.open(); return; }
    if (t.closest('[data-vc-copy]')) { e.preventDefault(); this.copy(); }
  }

  /* Shopify protects customer forms with invisible hCaptcha (storefront captcha bootstrap): it binds on
     focus, adds its token and submits the form natively. A background fetch has no token and gets
     rejected, so the native, captcha-protected post is the only submission. The page comes back with
     ?customer_posted=true and start() opens straight into the success state. */
  onSubmit(e) {
    const value = (this.input.value || '').trim();
    if (this.busy || !EMAIL_RE.test(value)) {
      e.preventDefault();
      e.stopImmediatePropagation(); /* keep the captcha handler from posting an invalid address */
      if (!this.busy) { this.showError(); this.input.focus(); }
      return;
    }
    this.input.value = value;
    this.clearError();
    this.setBusy(true);
    clearTimeout(this.busyTimer);
    /* if a visible captcha challenge is dismissed, the page never navigates: re-enable the button */
    this.busyTimer = setTimeout(() => this.setBusy(false), 10000);
  }

  async copy() {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(this.code); ok = true; }
    } catch (_) { /* fall through */ }
    if (!ok) {
      const ta = document.createElement('textarea');
      ta.value = this.code;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
      document.body.appendChild(ta);
      ta.select();
      try { ok = document.execCommand('copy'); } catch (_) { ok = false; }
      ta.remove();
    }
    if (!ok) {
      /* last resort: select the visible code so the visitor can copy it by hand */
      const codeEl = this.el.querySelector('.vc-signup__code-text');
      if (codeEl && window.getSelection) {
        const r = document.createRange(); r.selectNodeContents(codeEl);
        const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(r);
      }
      return;
    }
    if (this.copyLabel) this.copyLabel.textContent = this.copiedLabelText;
  }

  /* Theme editor */
  editorOpen() {
    this.setState(this.previewSuccess ? 'done' : 'form');
    this.open();
  }
  editorClose() { this.close(); }
}

/* ---------- Registry ---------- */
function find(sectionId) {
  for (const i of instances) if (i.sectionId === sectionId && i.el.isConnected) return i;
  return null;
}

function initAll() {
  for (const i of Array.from(instances)) {
    if (!i.el.isConnected) { i.dispose(); instances.delete(i); }
  }
  document.querySelectorAll('[data-vc-signup]').forEach((el) => {
    if (el.__vcSignup) return;
    const inst = new Signup(el);
    el.__vcSignup = inst;
    instances.add(inst);
  });
}

function envChanged() { instances.forEach((i) => i.onEnvChange()); }

/* ---------- Global listeners (registered once per page) ---------- */
document.addEventListener('keydown', (e) => {
  for (const i of instances) {
    if (!i.isOpen) continue;
    if (e.key === 'Escape') { e.preventDefault(); i.close(); return; }
    if (e.key === 'Tab') {
      const list = focusables(i.dialog);
      if (!list.length) { e.preventDefault(); i.dialog.focus(); return; }
      const first = list[0];
      const last = list[list.length - 1];
      const ae = document.activeElement;
      if (e.shiftKey && (ae === first || ae === i.dialog || !i.dialog.contains(ae))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (ae === last || !i.dialog.contains(ae))) { e.preventDefault(); first.focus(); }
      return;
    }
  }
});

document.addEventListener('focusin', (e) => {
  for (const i of instances) {
    if (i.isOpen && i.dialog && !i.dialog.contains(e.target)) {
      const list = focusables(i.dialog);
      (list[0] || i.dialog).focus({ preventScroll: true });
    }
  }
});

new MutationObserver(envChanged).observe(html, { attributes: true, attributeFilter: ['class'] });
new MutationObserver(envChanged).observe(document.documentElement, { attributes: true, attributeFilter: ['open'], subtree: true });

document.addEventListener('shopify:section:load', initAll);
document.addEventListener('shopify:section:unload', () => setTimeout(initAll, 0));
document.addEventListener('shopify:section:select', (e) => {
  const i = find(e.detail && e.detail.sectionId);
  if (i) i.editorOpen();
});
document.addEventListener('shopify:section:deselect', (e) => {
  const i = find(e.detail && e.detail.sectionId);
  if (i) i.editorClose();
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
else initAll();
